import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import Betslip from './betslip/index.js';
import NumericKeypad from 'modules/keypad/numeric-keypad/index.js';
import BetBuilder from './bet-builder/index.js';
import BetReceipt from 'modules/bet-receipt/index.js';
import Overask from 'modules/overask/index.js';

const { ScrollPane, FlexPane } = MojitoPresentation.Components;
const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const isShallowEqual = MojitoCore.Base.objUtils.isShallowEqual;

export default class BetslipView extends UIViewImplementation {
    constructor(props) {
        super(props);

        this.state = {
            targetScrollPosition: undefined,
            isKeypadClickable: true,
        };

        this.targetInput = undefined;
        // According to https://jira.pbs.one/browse/MOJ-6786, there is a case when scroll pane is not enough big for actual scrolling
        // But we react on touch-move events anyway and block keypad from interactions.
        // That is why `paneIsScrollable` variable will be as a marker that there are active scroll events and not only touch events
        // In case scrollPane has enough children for real scroll action, we will get `onscroll` event, make `paneIsScrollable` true and block keypad.
        // Right after scroll action will be finished, we make `paneIsScrollable` false again until next real scroll event.
        this.paneIsScrollable = false;
        this.activeScrollToTargetInput = undefined;

        this.onKeypadShow = this.onKeypadShow.bind(this);
        this.onKeypadHide = this.onKeypadHide.bind(this);
        this.onKeypadKeyClick = this.onKeypadKeyClick.bind(this);

        this.scrollFullscreenBetslipToTop = this.scrollFullscreenBetslipToTop.bind(this);
        this.scrollFullscreenBetslipToBottom = this.scrollFullscreenBetslipToBottom.bind(this);
        this.onScrollPaneScroll = this.onScrollPaneScroll.bind(this);
        this.onScrollPaneResize = this.onScrollPaneResize.bind(this);
        this.onScrollPaneTouchMove = this.onScrollPaneTouchMove.bind(this);
        this.onScrollFinished = this.onScrollFinished.bind(this);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isShallowEqual(this.props, nextProps) || !isShallowEqual(this.state, nextState);
    }

    onScrollFinished() {
        this.activeScrollToTargetInput = undefined;
        this.paneIsScrollable = false;
        this.setState({ isKeypadClickable: true });
    }

    scrollFullscreenBetslipToTop() {
        this.setState({ targetScrollPosition: 0 });
    }

    scrollFullscreenBetslipToBottom() {
        this.setState({ targetScrollPosition: Infinity });
    }

    scrollToTargetInput(forceScroll = false) {
        if (!this.targetInput) {
            return;
        }

        // prevents unneeded calculations if targetInput element is the same
        // [forceScroll=false] is the option to ignore optimization.
        //    - possible usage is scroll to element when layout was changed(suspended bets, added notifications)
        if (!forceScroll && this.activeScrollToTargetInput === this.targetInput) {
            return;
        }

        this.targetInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.activeScrollToTargetInput = this.targetInput;
    }

    onKeypadShow(targetInputId) {
        this.targetInput = document.getElementById(targetInputId);
        this.scrollToTargetInput();
    }

    onKeypadHide() {
        this.targetInput = undefined;
    }

    onKeypadKeyClick(targetInputId) {
        this.targetInput = document.getElementById(targetInputId);
        this.scrollToTargetInput();
    }

    onScrollPaneResize() {
        this.scrollToTargetInput();
    }

    onScrollPaneScroll() {
        this.paneIsScrollable = true;
    }

    onScrollPaneTouchMove() {
        if (this.state.isKeypadClickable && this.paneIsScrollable) {
            this.setState({ isKeypadClickable: false });
        }
    }

    renderWithScrollPane(child, additionalScrollPaneProps = {}) {
        return (
            <ScrollPane
                config={this.config.scrollPane}
                {...additionalScrollPaneProps}
                class="ta-BetslipScrollPane"
            >
                {child}
            </ScrollPane>
        );
    }

    renderBetReceipt() {
        const receipt = (
            <BetReceipt
                config={this.config.betReceipt}
                scrollFullscreenBetslipToTop={this.scrollFullscreenBetslipToBottom}
                onClear={this.props.onClearReceipt}
            />
        );
        return this.config.isScrollable ? this.renderWithScrollPane(receipt) : receipt;
    }

    renderOverask() {
        const overask = <Overask />;
        return this.config.isScrollable ? this.renderWithScrollPane(overask) : overask;
    }

    renderBetBuilder(config, props) {
        return (
            <BetBuilder
                config={config.betBuilder}
                betslip={props.betslip}
                hasPendingBonus={props.hasPendingBonus}
                stakeGroup={props.stakeGroup}
                currencyCode={props.currencyCode}
                betslipStatus={props.betslipStatus}
                bankersActivated={props.bankersActivated}
                isStakeGroupPending={props.isStakeGroupPending}
                hasPendingBankerBet={props.hasPendingBankerBet}
            />
        );
    }

    renderBetslip() {
        const scrollPaneProps = {
            targetPosition: this.state.targetScrollPosition,
            onScroll: this.onScrollPaneScroll,
            onResize: this.onScrollPaneResize,
            onScrollFinished: this.onScrollFinished,
            onTouchMove: this.onScrollPaneTouchMove,
        };

        return (
            <Betslip
                config={this.config.betslip}
                betslip={this.props.betslip}
                scrollFullscreenBetslipToBottom={this.scrollFullscreenBetslipToBottom}
                currencyCode={this.props.currencyCode}
                freeBets={this.props.freeBets}
                betPlacementButtonDisabled={this.props.betPlacementButtonDisabled}
                stakeGroup={this.props.stakeGroup}
                hasOddsChangeBet={this.props.hasOddsChangeBet}
                hasBoostedHcapChange={this.props.hasBoostedHcapChange}
                hasHcapChangeBet={this.props.hasHcapChangeBet}
                isSuspended={this.props.isSuspended}
                isExpired={this.props.isExpired}
                betslipStatus={this.props.betslipStatus}
                commitBetslipMode={this.props.commitBetslipMode}
                validationErrors={this.props.validationErrors}
                onAcceptTechnicalError={this.props.onAcceptTechnicalError}
                scrollPaneProps={scrollPaneProps}
            >
                {this.renderBetBuilder(this.config, this.props)}
            </Betslip>
        );
    }

    render() {
        if (this.props.showOverask) {
            return this.renderOverask();
        }

        if (this.props.showReceipt) {
            return this.renderBetReceipt();
        }

        return (
            <FlexPane config={this.config.container} class="ta-betslipView">
                {this.renderBetslip()}
                <NumericKeypad
                    onShow={this.onKeypadShow}
                    onHide={this.onKeypadHide}
                    onKeyClick={this.onKeypadKeyClick}
                    clickable={this.state.isKeypadClickable}
                />
            </FlexPane>
        );
    }
}
